// @flow

import React, {useState, useEffect, useRef} from "react";
import {Table, Form, Input, Switch, Button} from "antd";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CompanyContanier, CompanyButtonContainer} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const LocationCheckOptions = ({companyId}) => {
    const stores = useStore();
    const {t} = useTranslation(["locationCheckOptions"]);

    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [companyRecall, setCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                locationCheck: {
                    isActive: companyData?.data?.options?.locationCheck?.isActive,
                    limit: companyData?.data?.options?.locationCheck?.limit
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            await stores.companyStore.updateCompanyLocationCheckOptions(values);

            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    return (
        <div data-testid="locationCheckOptionsScreen">
            <h2>{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="localtionCheckOptionsForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        data-testid="locationCheckOptionsTable"
                        pagination={false}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={`${t(
                                    "locationCheckOptions.fields.locationCheckOptions.label",
                                    {ns: "locationCheckOptions"}
                                )} (${t("locationCheckOptions.fields.locationCheckOptions.short", {
                                    ns: "locationCheckOptions"
                                })})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.locationCheck?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="locationCheckOptionSwitch"
                                            onChange={e => {
                                                setFieldValue("options.locationCheck.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={t("locationCheckOptions.fields.limit", {
                                    ns: "locationCheckOptions"
                                })}
                                dataIndex="limit"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.locationCheck.limit`) &&
                                            getIn(touched, `options.locationCheck.limit`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.locationCheck.limit`) &&
                                            getIn(touched, `options.locationCheck.limit`)
                                                ? getIn(errors, `options.locationCheck.limit`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="limit"
                                            data-testid="limitInput"
                                            value={
                                                values.options?.locationCheck?.limit
                                                    ? values.options?.locationCheck?.limit
                                                    : companyData?.data?.options?.locationCheck
                                                          ?.limit
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.locationCheck.limit",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options?.locationCheck?.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                        </FormikProvider>
                    </Table>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
        </div>
    );
};
